import React, { useState } from 'react';
import { Loader2, AlertCircle, CreditCard } from 'lucide-react';
import { paymentCardGradient } from '@/components/payment/PaymentCardGradientStyles';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { supabase } from '@/lib/supabase';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontWeight: '500',
      '::placeholder': {
        color: 'rgba(255, 255, 255, 0.4)',
      },
    },
    invalid: {
      color: '#f87171',
      iconColor: '#f87171',
    },
  },
  hidePostalCode: false,
};

function AddCardForm({ onClose, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsProcessing(true);

    try {
      if (!stripe || !elements) {
        throw new Error('Payment system not ready');
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Please sign in to continue');
      }

      const { reserveUrl } = await import('@/lib/apiUrls');
      const { mergeSupabaseEdgeHeaders } = await import('@/lib/reserveActAs');
      const response = await fetch(reserveUrl('create-setup-intent'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to set up card');
      }

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card input not found');
      }

      const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(
        data.client_secret,
        {
          payment_method: {
            card: cardElement,
          }
        }
      );

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      if (setupIntent.status === 'succeeded') {
        toast.success('Card added successfully!');
        onSuccess?.();
        onClose();
      } else {
        toast.info('Card setup is being processed');
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className={paymentCardGradient.container}>
        <p className="text-white/70 text-xs font-normal uppercase tracking-wider mb-4">Card details</p>
        <div className={paymentCardGradient.cardElementWrapper}>
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl">
          <p className="text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {error}
          </p>
        </div>
      )}

      <div className="flex items-center gap-2 text-white/60 text-sm">
        <CreditCard className="w-4 h-4" />
        <span>Your card will be securely saved for future payments</span>
      </div>

      <div className="flex gap-3">
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          className="flex-1 border border-white/20 text-white hover:bg-white/10 hover:text-white rounded-full py-6 bg-transparent"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isProcessing || !stripe}
          className="flex-1 bg-white text-black hover:bg-white/90 rounded-full py-6 disabled:opacity-50"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Add Card'
          )}
        </Button>
      </div>
    </form>
  );
}

export default function AddCardModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  stripePromise
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-brand-950/95 border-brand-800/45 backdrop-blur-xl text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-light">Add Card</DialogTitle>
          <DialogDescription className="text-white/60 font-normal">
            Add a new card to your account
          </DialogDescription>
        </DialogHeader>
        
        {stripePromise ? (
          <Elements stripe={stripePromise}>
            <AddCardForm onClose={onClose} onSuccess={onSuccess} />
          </Elements>
        ) : (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-white/40" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
